import { useRef, useEffect, useMemo } from 'react';
import { HOUR_HEIGHT_PX } from '../types';
import type { EventInstance } from '../types';
import type { TimeFormat } from '../types';
import {
  positionTimedEvents,
  isToday,
  formatDateKey,
  formatTime,
  findOverlappingEventIds,
  findInsufficientBreaks,
  startOfDay,
  endOfDay,
} from '../utils';
import { TimeGridEvent } from './TimeGridEvent';

interface TimeGridProps {
  days: Date[];
  events: EventInstance[];
  workingHoursStart: number;
  timeFormat?: TimeFormat;
  onClickSlot: (date: Date, hour: number) => void;
  onClickEvent: (event: EventInstance) => void;
  resizingEventId?: string | null;
  resizeHeight?: number | null;
  onResizeStart?: (eventId: string, originalHeight: number, e: React.MouseEvent) => void;
  hourHeightPx?: number;
  dimPastEvents?: boolean;
  eventColorPriority?: 'event' | 'calendar';
  showConflictWarnings?: boolean;
  minBreakBetweenEvents?: number;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);

export function TimeGrid({
  days,
  events,
  workingHoursStart,
  timeFormat = '12h',
  onClickSlot,
  onClickEvent,
  resizingEventId,
  resizeHeight,
  onResizeStart,
  hourHeightPx: hourHeight = HOUR_HEIGHT_PX,
  dimPastEvents = false,
  eventColorPriority = 'event',
  showConflictWarnings = false,
  minBreakBetweenEvents = 0,
}: TimeGridProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Compute which events have conflicts across all visible days
  const conflictingIds = useMemo(() => {
    if (!showConflictWarnings) return new Set<string>();
    const allIds = new Set<string>();
    for (const day of days) {
      const dayStart = startOfDay(day);
      const dayEnd = endOfDay(day);
      const dayEvents = events.filter((e) => {
        if (e.isAllDay) return false;
        const s = new Date(e.startAt);
        const end = new Date(e.endAt);
        return s < dayEnd && end > dayStart;
      });
      const ids = findOverlappingEventIds(dayEvents);
      ids.forEach((id) => allIds.add(id));
    }
    return allIds;
  }, [showConflictWarnings, days, events]);

  // Compute insufficient break gaps per day
  const breakWarningsByDay = useMemo(() => {
    if (minBreakBetweenEvents <= 0)
      return new Map<string, ReturnType<typeof findInsufficientBreaks>>();
    const map = new Map<string, ReturnType<typeof findInsufficientBreaks>>();
    for (const day of days) {
      const warnings = findInsufficientBreaks(events, day, minBreakBetweenEvents);
      if (warnings.length > 0) {
        map.set(formatDateKey(day), warnings);
      }
    }
    return map;
  }, [minBreakBetweenEvents, days, events]);

  // Auto-scroll to working hours on mount
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = workingHoursStart * hourHeight - 20;
    }
  }, [workingHoursStart, hourHeight]);

  return (
    <div ref={scrollRef} className="flex-1 overflow-auto">
      <div className="flex" style={{ minHeight: 24 * hourHeight }}>
        {/* Hour labels */}
        <div className="w-14 shrink-0">
          {HOURS.map((hour) => (
            <div
              key={hour}
              className="text-[10px] text-muted-foreground text-right pr-2 relative"
              style={{ height: hourHeight }}
            >
              <span className="absolute -top-[7px] right-2">
                {hour === 0 ? '' : formatTime(new Date(2000, 0, 1, hour), timeFormat)}
              </span>
            </div>
          ))}
        </div>

        {/* Day columns */}
        <div
          className="flex-1 grid relative"
          style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)` }}
        >
          {days.map((day) => {
            const dayKey = formatDateKey(day);
            const positioned = positionTimedEvents(events, day, hourHeight);
            const todayClass = isToday(day) ? 'bg-primary/5' : '';

            return (
              <div key={dayKey} className={`border-r last:border-r-0 relative ${todayClass}`}>
                {/* Hour grid lines */}
                {HOURS.map((hour) => (
                  <div
                    key={hour}
                    role="button"
                    tabIndex={0}
                    aria-label={formatTime(new Date(2000, 0, 1, hour), timeFormat)}
                    className="border-b border-dashed border-muted cursor-pointer hover:bg-muted/30"
                    style={{ height: hourHeight }}
                    onClick={() => onClickSlot(day, hour)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onClickSlot(day, hour);
                      }
                    }}
                  />
                ))}

                {/* Positioned events */}
                {positioned.map((pos) => (
                  <TimeGridEvent
                    key={pos.event.id}
                    positioned={pos}
                    onClick={() => onClickEvent(pos.event)}
                    timeFormat={timeFormat}
                    resizingHeight={resizingEventId === pos.event.id ? resizeHeight : undefined}
                    onResizeStart={onResizeStart}
                    dimPastEvents={dimPastEvents}
                    eventColorPriority={eventColorPriority}
                    hasConflict={conflictingIds.has(pos.event.eventId)}
                  />
                ))}

                {/* Insufficient break indicators */}
                {breakWarningsByDay.get(dayKey)?.map((w, i) => {
                  const endBefore = new Date(w.eventBefore.endAt);
                  const startAfter = new Date(w.eventAfter.startAt);
                  const midMinutes =
                    (endBefore.getHours() * 60 +
                      endBefore.getMinutes() +
                      startAfter.getHours() * 60 +
                      startAfter.getMinutes()) /
                    2;
                  const topPx = (midMinutes / 60) * hourHeight;

                  return (
                    <div
                      key={`break-${i}`}
                      className="absolute left-1 right-1 flex items-center justify-center pointer-events-none z-10"
                      style={{ top: topPx - 6, height: 12 }}
                      title={`${w.gapMinutes}min break`}
                    >
                      <div className="border-t border-dashed border-amber-400 dark:border-amber-500 w-full" />
                      <span className="absolute bg-amber-100 dark:bg-amber-900/60 text-amber-700 dark:text-amber-300 text-[9px] px-1 rounded">
                        {w.gapMinutes}m
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
